import * as React from 'react';
import { Check, ChevronDown, CircleX, X } from 'lucide-react';
import { cn } from '../../lib/utils';

type Option = {
  value: string;
  label: string;
};

type MultiSelectProps = {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  showClear?: boolean; // Optional prop to show clear button
};

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className,
  showClear = true,
}) => {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleSelect = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <div ref={containerRef} className={cn('relative w-48', className)}>
      <button
        type="button"
        className={cn(
          'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        )}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="truncate max-w-[70%]">
          {value.length === 0
            ? placeholder
            : options
                .filter((o) => value.includes(o.value))
                .map((o) => o.label)
                .join(', ')}
        </span>
        <div className="flex items-center gap-1">
          {showClear && value.length > 0 && (
            <button
              type="button"
              aria-label="Clear selection"
              className="p-0.5 rounded hover:bg-muted"
              onClick={handleClear}
              tabIndex={-1}
            >
              <CircleX className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
          <ChevronDown className="h-4 w-4 opacity-50" />
        </div>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md max-h-96 overflow-auto">
          {options.map((option) => (
            <div
              key={option.value}
              className={cn(
                'flex justify-between items-center cursor-pointer select-none py-1.5 pl-2 pr-2 text-sm hover:bg-accent m-1 rounded-sm',
                value.includes(option.value) && 'bg-accent text-accent-foreground',
              )}
              onClick={() => handleSelect(option.value)}
            >
              <div className="ml-2">{option.label}</div>
              <div className="flex h-3.5 w-3.5 items-center justify-end">
                {value.includes(option.value) && <Check className="h-4 w-4" />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
